// @ts-check
import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import log from 'loglevel';
import { useClassroomTracking } from 'Util/Analytics';
import { isValidQuality } from 'Util/videoPreferencesStorage';
import { VIDEO_STATE } from '../constant';
import { PLAYER_STATE } from './PlayerState';
import toImageData from './toImageData';

const LearnCraftVideoPlayerContext = createContext(
  /**@type {ILearnCraftVideoPlayerContext|null}*/ (null)
);
/** @param {IframeMessageEventName} event */
function mapEventToVideoState(event) {
  switch (event) {
    case 'video_play':
      return VIDEO_STATE.PLAY;
    case 'video_pause':
      return VIDEO_STATE.PAUSED;
    case 'video_end':
      return VIDEO_STATE.ENDED;
    case 'video_loaded_data':
      return VIDEO_STATE.READY;
    case 'video_error':
      return VIDEO_STATE.ERROR;
    default:
      return null;
  }
}
/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
function LearnCraftVideoPlayerProvider({ children }) {
  const { logSubsectionVideoInteraction } = useClassroomTracking();
  const [playerState, setPlayerState] = useState(
    /** @type {PlayerState} */ (PLAYER_STATE.INITIALIZE)
  );
  const [videoState, _setVideoState] = useState(/** @type {VideoState} */ (VIDEO_STATE.LOAD));
  const setVideoState = (/** @type {VideoState} */ videoState) => {
    log.debug(`[LearnCraftVideoPlayerProvider] set videoState: ${videoState}`);
    _setVideoState(videoState);
  };
  const currentTimeRef = useRef(/**@type {number|null}*/ (null));
  const currentSubVideoIdRef = useRef(/**@type {string|null}*/ (null));
  const playerRef = useRef(/**@type {HTMLIFrameElement|null}*/ (null));
  const videoSrcRef = useRef('');
  const videoOriginRef = useRef('');
  /** @param {number} time */
  const setCurrentTime = (time) => {
    currentTimeRef.current = time;
  };
  /** @param {MessageEvent<IframeMessageData>} message */
  const handleVideoEvent = (message) => {
    log.trace(`[LearnCraftVideoPlayerProvider] accept video event: ${message.data.event}`);
    if (message.origin !== videoOriginRef.current) return;
    log.trace(`[LearnCraftVideoPlayerProvider] match origin`);
    const info = message.data.info;
    if (info) {
      if (info.currentTime !== null && info.currentTime !== undefined) {
        setCurrentTime(info.currentTime);
      }
      if (info.videoAssetId) {
        currentSubVideoIdRef.current = info.videoAssetId;
      }
    }
    const videoState = mapEventToVideoState(message.data.event);
    if (videoState) {
      setVideoState(videoState);
    }
  };
  useEffect(() => {
    if (playerState === PLAYER_STATE.ACTIVE) {
      videoOriginRef.current = new URL(playerRef.current.src).origin;
      window.addEventListener('message', handleVideoEvent);
      if (playerRef.current) {
        // onload for detect when video src change
        //  - example use-case: user change Learning Modes and the mode have different video
        //  * FIXME: this `onload` may trigger after `video_play` event, if here set VideoState to `LOAD` player may working incorrect.
        //  *   maybe use `playing` event instead (for now only found this case in Safari)
        playerRef.current.onload = () => {
          log.debug(`[LearnCraftVideoPlayerProvider] playerRef onLoad`);
          const videoSrc = playerRef.current.src;
          if (videoSrcRef.current && videoSrcRef.current !== videoSrc) {
            setVideoState(VIDEO_STATE.LOAD);
          }
          videoSrcRef.current = videoSrc;
          // `videoOriginRef` may block message from new video. if it is set after accept message (but normally origin will be same)
          videoOriginRef.current = new URL(videoSrc).origin;
        };
      }
    } else if (playerState === PLAYER_STATE.INACTIVE) {
      window.removeEventListener('message', handleVideoEvent);
      currentTimeRef.current = null;
      setVideoState(VIDEO_STATE.LOAD);
      playerRef.current = null;
      currentSubVideoIdRef.current = null;
    }
    return () => {
      window.removeEventListener('message', handleVideoEvent);
    };
  }, [playerState]);
  const play = () => {
    if (playerRef.current) {
      playerRef.current.contentWindow.postMessage({ event: 'play_player' }, videoOriginRef.current);
    }
  };
  const pause = () => {
    if (playerRef.current) {
      playerRef.current.contentWindow.postMessage(
        { event: 'pause_player' },
        videoOriginRef.current
      );
    }
  };
  const focusPlayer = () => {
    playerRef.current?.contentWindow?.postMessage(
      { event: 'focus_player' },
      videoOriginRef.current
    );
  };
  /** @param {number} seekToSec */
  const seek = (seekToSec) => {
    if (playerRef.current) {
      playerRef.current.contentWindow.postMessage(
        { event: 'seek_player', seekToSec },
        videoOriginRef.current
      );
    }
  };

  // Push the allowed speeds to the player (odah owns BU logic); send nothing → player keeps default.
  /** @param {number[]} rates */
  const setPlaybackRates = (rates) => {
    if (playerRef.current && Array.isArray(rates) && rates.length > 0) {
      playerRef.current.contentWindow.postMessage(
        { event: 'set_playback_rates', rates },
        videoOriginRef.current
      );
    }
  };

  /** @param {number} rate */
  const selectPlaybackRate = (rate) => {
    if (playerRef.current && typeof rate === 'number' && rate > 0) {
      playerRef.current.contentWindow.postMessage(
        { event: 'select_playback_rate', rate },
        videoOriginRef.current
      );
    }
  };

  /** @param {'auto'|number} quality */
  const selectQuality = (quality) => {
    if (playerRef.current && isValidQuality(quality)) {
      playerRef.current.contentWindow.postMessage(
        { event: 'set_quality', quality },
        videoOriginRef.current
      );
    }
  };

  /**
   * Centralized replay command to LAMS (seek to start + play).
   * LAMS also handles Space replay when the player itself is focused.
   */
  const replay = () => {
    seek(0);
    play();
  };
  const capture = async () => {
    const controller = new AbortController();
    const signal = controller.signal;
    /** @type {Promise<LearnCraftScreenshotData>} */
    const getScreenshotFromLearnCraft = new Promise((res, reject) => {
      let timer = setTimeout(() => reject(), 2000);
      window.addEventListener(
        'message',
        /** @param {MessageEvent<IframeMessageData>} message */
        (message) => {
          const data = message.data;
          if (message.origin === videoOriginRef.current && data.event === 'screenshot') {
            clearTimeout(timer);
            res(data.info);
          }
        },
        { signal }
      );
    });
    if (playerRef.current) {
      playerRef.current.contentWindow.postMessage(
        { event: 'request_screenshot' },
        videoOriginRef.current
      );
    }
    return await getScreenshotFromLearnCraft
      .then(
        /** @returns {Promise<ScreenshotData>} */
        async ({ imageDataUrl, ...otherInfo }) => {
          controller.abort();
          const image = await toImageData(imageDataUrl);
          return { image, ...otherInfo };
        }
      )
      .catch(
        /** @returns {null} */ () => {
          controller.abort();
          return null;
        }
      );
  };
  const activate = () => {
    videoSrcRef.current = '';
    setPlayerState(PLAYER_STATE.ACTIVE);
  };
  return (
    // @ts-ignore
    <LearnCraftVideoPlayerContext.Provider
      value={{
        activate,
        deactivate: () => setPlayerState(PLAYER_STATE.INACTIVE),
        getCurrentTime: () => currentTimeRef.current,
        getCurrentSubVideoId: () => currentSubVideoIdRef.current,
        playerRef,
        playerState,
        videoState,
        play,
        pause,
        focusPlayer,
        seek,
        setPlaybackRates,
        selectPlaybackRate,
        selectQuality,
        replay,
        capture,
        logSubsectionVideoInteraction,
      }}
    >
      {children}
    </LearnCraftVideoPlayerContext.Provider>
  );
}
function useLearnCraftVideoPlayer() {
  const context = useContext(LearnCraftVideoPlayerContext);
  if (context == null) {
    throw new Error(
      'LearnCraftVideoPlayerContext must be used within LearnCraftVideoPlayerProvider'
    );
  }
  return context;
}
export { useLearnCraftVideoPlayer };
export default LearnCraftVideoPlayerProvider;
/**
 * @typedef ILearnCraftVideoPlayerContext
 * @property {() => void} activate
 * @property {() => void} deactivate
 * @property {React.MutableRefObject<HTMLIFrameElement|null>} playerRef
 * @property {() => number|null} getCurrentTime
 * @property {() => string|null} getCurrentSubVideoId
 * @property {PlayerState} playerState
 * @property {VideoState} videoState
 * @property {() => void} play
 * @property {() => void} pause
 * @property {() => void} focusPlayer
 * @property {(time: number) => void} seek
 * @property {(rates: number[]) => void} setPlaybackRates
 * @property {(rate: number) => void} selectPlaybackRate
 * @property {(quality: 'auto'|number) => void} selectQuality
 * @property {() => void} replay
 * @property {() => Promise< ScreenshotData | null >} capture
 * @property {(info: Record<string, any>) => void} logSubsectionVideoInteraction
 *
 * @typedef {{event: IframeMessageEventName, info?: {currentTime: number, videoAssetId?: string, [k:string]: any}}} IframeMessageData
 * @typedef {"video_play" | "video_pause" | "video_end" | "video_seeked" | "time_update" | "duration_change" | "video_loaded_data" | "playback_rate_changed" | "quality_changed" | "screenshot" | "video_error" | "volume_change" | "speed_change"} IframeMessageEventName
 *
 * @typedef {{source: string; file: Blob|null; name: string; type: string;}} ImageData
 *
 * @typedef {import('./PlayerState').PlayerState} PlayerState
 * @typedef {import('../constant').VideoState} VideoState
 */
/**
 * @typedef LearnCraftScreenshotData
 * @property {string} imageDataUrl
 * @property {?string} chapterName
 * @property {?string} pageNo
 * @property {?string} problemNo
 */
/**
 * @typedef ScreenshotData
 * @property {ImageData} image
 * @property {?string} chapterName
 * @property {?string} pageNo
 * @property {?string} problemNo
 */
